// SideDashboard — shared work surface for /supply and /demand.
//
// Top-to-bottom: three stacked KPI sections (S1/S2/S3 or D1/D2/D3), each
// with an inline live chart, a drill-down into must-win categories, vendor
// lists per category, and per-section action filters. Below all three: the
// side-filtered global accountability ledger.
//
// Action impacts are hierarchical (see lib/actions.ts). Click into a vendor
// row → its in-flight actions render right there, and "+ Add action" from
// the row pre-fills impact = "s1:fuel:shell" automatically. Category- or
// KPI-level actions appear on every drill-down they touch.

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  STATUS_COLOR as ACTION_STATUS_COLOR,
  STATUS_LABEL as ACTION_STATUS_LABEL,
  actionInSideScope,
  actionTouchesPath,
  addAction,
  deleteAction,
  listActions,
  updateAction,
  type ActionRow,
  type ActionStatus,
} from "../lib/actions";
import {
  CATEGORIES,
  FIT_COLOR,
  FIT_LABEL,
  PRIORITY_COLOR,
  PRIORITY_LABEL,
  deriveFit,
  findCategory,
  type Category,
  type CategoryFit,
} from "../lib/categories";
import {
  DEMAND_SURFACE_KPIS,
  STATUS_COLOR,
  STATUS_LABEL,
  SUPPLY_SURFACE_KPIS,
  type CategoryMetricKey,
  type Kpi,
  type ValueIndicator,
} from "../lib/kpis";
import { resolveIndicator, type ResolvedValue } from "../lib/kpi-values";
import {
  listCatalogQueries,
  runCatalogQuery,
  type CatalogBundle,
  type CatalogEntry,
  type QueryRunResult,
} from "../lib/query-catalog";
import {
  STATUS_COLOR as VENDOR_STATUS_COLOR,
  STATUS_LABEL as VENDOR_STATUS_LABEL,
  listVendors,
  upsertVendor,
  vendorsForCategory,
  type Vendor,
  type VendorStatus,
} from "../lib/vendors";
import { useAuth } from "./AuthGate";
import { Chart } from "./Chart";
import { DistributionChart } from "./DistributionChart";
import { LiabilityChart } from "./LiabilityChart";
import { RetentionChart } from "./RetentionChart";
import { SettlementChart } from "./SettlementChart";
import { SymmetryChart } from "./SymmetryChart";
import { ThresholdChart } from "./ThresholdChart";

type ResolvedMap = Record<string, ResolvedValue>;
type ChartMap = Record<string, { entry: CatalogEntry; result: QueryRunResult | null; loading: boolean; error: string | null }>;

interface Props {
  side: "supply" | "demand";
}

function indKey(kpiId: string, i: number): string {
  return `${kpiId}:${i}`;
}

export function SideDashboard({ side }: Props) {
  const { session } = useAuth();
  const currentUser = session?.email ?? "unknown";

  const [resolved, setResolved] = useState<ResolvedMap>({});
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [charts, setCharts] = useState<ChartMap>({});

  const kpis = side === "supply" ? SUPPLY_SURFACE_KPIS : DEMAND_SURFACE_KPIS;

  // Resolve headline indicator for each KPI.
  useEffect(() => {
    let alive = true;
    (async () => {
      for (const kpi of kpis) {
        const ind = kpi.valueIndicators[0] as ValueIndicator | undefined;
        if (!ind) continue;
        const rv = await resolveIndicator(ind);
        if (!alive) return;
        setResolved((prev) => ({ ...prev, [indKey(kpi.id, 0)]: rv }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [kpis]);

  // Load catalog and run the bound chart query per KPI in parallel.
  useEffect(() => {
    let alive = true;
    (async () => {
      let catalog: CatalogBundle;
      try {
        catalog = await listCatalogQueries();
      } catch {
        return;
      }
      for (const kpi of kpis) {
        const ind = kpi.valueIndicators[0] as ValueIndicator | undefined;
        if (!ind?.queryId) continue;
        const entry = catalog.queries.find((q) => q.id === ind.queryId);
        if (!entry || !entry.sql) continue;
        setCharts((prev) => ({
          ...prev,
          [kpi.id]: { entry, result: null, loading: true, error: null },
        }));
        try {
          const result = await runCatalogQuery(entry.id);
          if (!alive) return;
          setCharts((prev) => ({
            ...prev,
            [kpi.id]: { entry, result, loading: false, error: null },
          }));
        } catch (e) {
          if (!alive) return;
          setCharts((prev) => ({
            ...prev,
            [kpi.id]: { entry, result: null, loading: false, error: String(e) },
          }));
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [kpis]);

  // Load actions + vendors.
  const refreshActions = useCallback(async () => {
    setActions(await listActions());
  }, []);
  const refreshVendors = useCallback(async () => {
    setVendors(await listVendors());
  }, []);

  useEffect(() => {
    void refreshActions();
    void refreshVendors();
  }, [refreshActions, refreshVendors]);

  const sideActions = useMemo(
    () => actions.filter((a) => actionInSideScope(a, side)),
    [actions, side],
  );

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__meta">
            {side === "supply" ? "SUPPLY · ISSUANCE" : "DEMAND · REDEMPTION"} ·{" "}
            {sideActions.length} ACTION{sideActions.length === 1 ? "" : "S"} IN FLIGHT
          </div>
          <h1>{side === "supply" ? "Supply" : "Demand"}</h1>
        </div>
      </div>

      <div style={kpiStack}>
        {kpis.map((kpi) => (
          <KpiSection
            key={kpi.id}
            kpi={kpi}
            side={side}
            resolved={resolved[indKey(kpi.id, 0)]}
            chart={charts[kpi.id]}
            actions={actions}
            vendors={vendors}
            currentUser={currentUser}
            onActionsChanged={refreshActions}
            onVendorsChanged={refreshVendors}
          />
        ))}
      </div>

      <SideLedger
        actions={sideActions}
        currentUser={currentUser}
        onChange={refreshActions}
      />
    </>
  );
}

// =================== KPI section ========================================

function KpiSection({
  kpi,
  side,
  resolved,
  chart,
  actions,
  vendors,
  currentUser,
  onActionsChanged,
  onVendorsChanged,
}: {
  kpi: Kpi;
  side: "supply" | "demand";
  resolved: ResolvedValue | undefined;
  chart: ChartMap[string] | undefined;
  actions: ActionRow[];
  vendors: Vendor[];
  currentUser: string;
  onActionsChanged: () => Promise<void>;
  onVendorsChanged: () => Promise<void>;
}) {
  const accent = side === "supply" ? "var(--purple)" : "var(--pink)";
  const color = STATUS_COLOR[kpi.status];
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [addingForKpi, setAddingForKpi] = useState(false);

  const kpiActions = useMemo(
    () => actions.filter((a) => actionTouchesPath(a, kpi.id)),
    [actions, kpi.id],
  );

  return (
    <section style={sectionWrap}>
      {/* Header line */}
      <div style={sectionHead}>
        <span style={kpiChip(accent)}>{kpi.code}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={sectionTitle}>{kpi.title}</div>
          <div style={sectionSubtitle}>{kpi.question ?? kpi.valueIndicators[0]?.label}</div>
        </div>
        <Headline rv={resolved} />
        <span style={rowDot(color)} />
        <span style={{ ...mono, color, fontSize: 9 }}>{STATUS_LABEL[kpi.status]}</span>
      </div>

      {/* Strategic call — the inline intelligence layer. Each block earns
          its place by interpreting its own data, not by being a dashboard tile. */}
      {kpi.strategicCall && (
        <div style={strategicCallBand(accent)}>
          <span style={strategicCallTag(accent)}>STRATEGIC CALL</span>
          <span style={strategicCallText}>{kpi.strategicCall}</span>
        </div>
      )}

      {/* Distribution chart — only on S1 (earn) and D1 (spend). The "today
          vs target" weight shift IS the answer this block is being asked.
          Each row carries its own commitment lifecycle (propose → agree /
          disagree → owner → blocker) — the table IS the meeting agenda. */}
      {kpi.id === "s1" && (
        <DistributionChart
          kind="earn"
          kpiId="s1"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}
      {kpi.id === "d1" && (
        <DistributionChart
          kind="spend"
          kpiId="d1"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}
      {kpi.id === "s2" && (
        <ThresholdChart
          kpiId="s2"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}
      {kpi.id === "s3" && (
        <LiabilityChart
          kpiId="s3"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}
      {kpi.id === "d2" && (
        <RetentionChart
          kpiId="d2"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}
      {kpi.id === "d3" && (
        <SettlementChart
          kpiId="d3"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}
      {kpi.id === "sym" && (
        <SymmetryChart
          kpiId="sym"
          actions={actions}
          currentUser={currentUser}
          onCommitmentChanged={onActionsChanged}
        />
      )}

      {/* Two-column body: chart on left, categories drill-down on right.
          Now hidden on every block — each KPI has its own row-commitment
          table above. The two-column body is dead code at this point but
          kept gated behind `false` until we're certain we don't need it. */}
      {false && (
        <div style={sectionBody}>
          <div style={chartCol}>
            <div style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>LIVE TRACKING</div>
            <KpiChart chart={chart} />
          </div>

          <div style={categoryCol}>
            <div style={categoryColHead}>
              <span style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>
                MUST-WIN CATEGORIES
              </span>
              {kpi.categoryMetricLabel && (
                <span style={categoryMetricColHead}>{kpi.categoryMetricLabel} →</span>
              )}
            </div>
            <div style={categoryList}>
              {CATEGORIES.map((cat) => (
                <CategoryBlock
                  key={cat.code}
                  kpi={kpi}
                  category={cat}
                  side={side}
                  vendors={vendors}
                  actions={actions}
                  isOpen={openCategory === cat.code}
                  onToggle={() =>
                    setOpenCategory((c) => (c === cat.code ? null : cat.code))
                  }
                  currentUser={currentUser}
                  onActionsChanged={onActionsChanged}
                  onVendorsChanged={onVendorsChanged}
                  accent={accent}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KPI-level actions (those tagged "s1" alone, not category- or vendor-scoped) */}
      <div style={kpiActionsBar}>
        <span style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>
          KPI-WIDE ACTIONS · {kpiActions.length}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" style={addBtnSmall} onClick={() => setAddingForKpi((v) => !v)}>
          {addingForKpi ? "Cancel" : "+ Add KPI-wide action"}
        </button>
      </div>
      {addingForKpi && (
        <ActionInlineForm
          mode="add"
          prefillImpacts={[kpi.id]}
          author={currentUser}
          onClose={() => setAddingForKpi(false)}
          onSaved={async () => {
            await onActionsChanged();
            setAddingForKpi(false);
          }}
        />
      )}
      {kpiActions
        .filter((a) => a.impacts.includes(kpi.id) && !a.impacts.some((i) => i.startsWith(kpi.id + ":")))
        .slice(0, 3)
        .map((a) => (
          <ActionLineRO key={a.id} row={a} onChanged={onActionsChanged} accent={accent} />
        ))}
    </section>
  );
}

// =================== Inline chart ========================================

function KpiChart({ chart }: { chart: ChartMap[string] | undefined }) {
  if (!chart) {
    return (
      <div style={chartPlaceholder}>
        <span style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>
          AWAITING QUERY · No SQL bound yet
        </span>
      </div>
    );
  }
  if (chart.loading) {
    return (
      <div style={chartPlaceholder}>
        <span style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>Loading…</span>
      </div>
    );
  }
  if (chart.error) {
    return (
      <div style={chartPlaceholder}>
        <span style={{ ...mono, fontSize: 9, color: "var(--coral)" }}>
          ERROR · {chart.error.slice(0, 80)}
        </span>
      </div>
    );
  }
  if (!chart.result) return null;
  return (
    <div style={{ width: "100%" }}>
      <Chart spec={chart.entry.chart} result={chart.result} />
    </div>
  );
}

// =================== Category block ======================================

function CategoryBlock({
  kpi,
  category,
  side,
  vendors,
  actions,
  isOpen,
  onToggle,
  currentUser,
  onActionsChanged,
  onVendorsChanged,
  accent,
}: {
  kpi: Kpi;
  category: Category;
  side: "supply" | "demand";
  vendors: Vendor[];
  actions: ActionRow[];
  isOpen: boolean;
  onToggle: () => void;
  currentUser: string;
  onActionsChanged: () => Promise<void>;
  onVendorsChanged: () => Promise<void>;
  accent: string;
}) {
  const catVendors = useMemo(
    () => vendorsForCategory(vendors, category.code, side),
    [vendors, category.code, side],
  );
  const focusPath = `${kpi.id}:${category.code}`;
  const liveCount = catVendors.filter((v) => v.status === "live" || v.status === "signed").length;
  const totalActive = catVendors.filter((v) => v.status !== "off-list").length;
  const blockedCount = catVendors.filter((v) => v.status === "blocked").length;
  const catActions = useMemo(
    () => actions.filter((a) => actionTouchesPath(a, focusPath)),
    [actions, focusPath],
  );

  const fit = deriveFit(category);
  const blockMetric = computeBlockMetric(kpi.categoryMetricKey, category, catActions.length, fit);

  return (
    <div style={categoryBlock}>
      <button type="button" onClick={onToggle} style={categoryRow(isOpen, accent)}>
        <span style={caret(isOpen)}>▸</span>
        <span style={priorityChip(category.priority)}>{PRIORITY_LABEL[category.priority]}</span>
        <span style={categoryLabel}>{category.label}</span>
        <span style={fitChip(fit)}>{FIT_LABEL[fit]}</span>
        <span style={ownerChip} title={`Owner: ${category.owner}`}>{category.owner}</span>
        <span style={categoryStats}>
          <span style={blockMetricStyle(blockMetric.tone)}>
            <span style={blockMetricValue}>{blockMetric.text}</span>
            {blockMetric.sub && <span style={blockMetricSub}>{blockMetric.sub}</span>}
          </span>
          {liveCount + totalActive > 0 && (
            <span style={statBlock} title="Live vendors / total active">
              <b>{liveCount}</b>/<b>{totalActive}</b>
            </span>
          )}
          {blockedCount > 0 && (
            <span style={{ ...statBlock, color: "var(--coral)" }} title="Blocked vendors">
              <b>{blockedCount}</b>✕
            </span>
          )}
          {catActions.length > 0 && (
            <span style={{ ...statBlock, color: accent }} title="Actions tagged to this category × KPI">
              <b>{catActions.length}</b>⚡
            </span>
          )}
        </span>
      </button>

      {isOpen && (
        <div style={categoryBody}>
          <div style={categoryDesc}>{category.description}</div>
          {category.anchor && (
            <div style={categoryAnchor}>
              <span style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>ANCHOR ·</span>{" "}
              {category.anchor}
            </div>
          )}
          {catVendors.length === 0 ? (
            <div style={emptyState}>No vendors in this category yet.</div>
          ) : (
            catVendors.map((v) => (
              <VendorBlock
                key={v.id}
                vendor={v}
                kpi={kpi}
                category={category}
                actions={actions}
                currentUser={currentUser}
                onActionsChanged={onActionsChanged}
                onVendorsChanged={onVendorsChanged}
                accent={accent}
              />
            ))
          )}
          <AddVendorButton
            kpi={kpi}
            category={category}
            side={side}
            currentUser={currentUser}
            onChanged={onVendorsChanged}
          />
        </div>
      )}
    </div>
  );
}

// =================== Vendor block (per-vendor drill) ======================

function VendorBlock({
  vendor,
  kpi,
  category,
  actions,
  currentUser,
  onActionsChanged,
  onVendorsChanged,
  accent,
}: {
  vendor: Vendor;
  kpi: Kpi;
  category: Category;
  actions: ActionRow[];
  currentUser: string;
  onActionsChanged: () => Promise<void>;
  onVendorsChanged: () => Promise<void>;
  accent: string;
}) {
  const focusPath = `${kpi.id}:${category.code}:${vendor.id}`;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  const vendorActions = useMemo(
    () => actions.filter((a) => actionTouchesPath(a, focusPath)),
    [actions, focusPath],
  );

  if (editing) {
    return (
      <div style={vendorWrap}>
        <VendorEditForm
          vendor={vendor}
          currentUser={currentUser}
          onSaved={async () => {
            await onVendorsChanged();
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div style={vendorWrap}>
      <div
        style={vendorRow}
        onClick={() => setOpen((v) => !v)}
        title="Click to see in-flight actions for this vendor"
      >
        <span style={caret(open)}>▸</span>
        <span style={vendorName}>{vendor.name}</span>
        <span style={vendorStatusPill(vendor.status)}>{VENDOR_STATUS_LABEL[vendor.status]}</span>
        <span style={vendorOwner}>{vendor.owner || <em style={{ color: "var(--ink-faint)" }}>unassigned</em>}</span>
        {vendorActions.length > 0 && (
          <span style={vendorActionCount(accent)}>
            {vendorActions.length} action{vendorActions.length === 1 ? "" : "s"}
          </span>
        )}
        <button
          type="button"
          style={pencilBtn}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          title="Edit vendor"
        >
          ✎
        </button>
      </div>

      {open && (
        <div style={vendorBody}>
          {vendor.notes && <div style={vendorNotes}>{vendor.notes}</div>}
          <div style={vendorActionsHeader}>
            <span style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>
              IN-FLIGHT ACTIONS · {vendorActions.length}
            </span>
            <span style={{ flex: 1 }} />
            <button type="button" style={addBtnSmall} onClick={() => setAdding((v) => !v)}>
              {adding ? "Cancel" : "+ Add action"}
            </button>
          </div>
          {adding && (
            <ActionInlineForm
              mode="add"
              prefillImpacts={[focusPath]}
              author={currentUser}
              onClose={() => setAdding(false)}
              onSaved={async () => {
                await onActionsChanged();
                setAdding(false);
              }}
            />
          )}
          {vendorActions.length === 0 && !adding && (
            <div style={emptyState}>No actions on this vendor yet.</div>
          )}
          {vendorActions.map((a) => (
            <ActionLineRO key={a.id} row={a} onChanged={onActionsChanged} accent={accent} />
          ))}
        </div>
      )}
    </div>
  );
}

// =================== Add vendor inline ===================================

function AddVendorButton({
  kpi: _kpi,
  category,
  side,
  currentUser,
  onChanged,
}: {
  kpi: Kpi;
  category: Category;
  side: "supply" | "demand";
  currentUser: string;
  onChanged: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  if (adding) {
    return (
      <VendorEditForm
        vendor={{
          id: "",
          categoryCode: category.code,
          sides: [side],
          name: "",
          status: "pipeline",
          owner: "",
          notes: "",
          updatedAt: "",
          updatedBy: currentUser,
        }}
        currentUser={currentUser}
        onSaved={async () => {
          await onChanged();
          setAdding(false);
        }}
        onCancel={() => setAdding(false)}
      />
    );
  }
  return (
    <button type="button" style={addVendorBtn} onClick={() => setAdding(true)}>
      + Add vendor to {category.label}
    </button>
  );
}

// =================== Vendor edit form =====================================

function VendorEditForm({
  vendor,
  currentUser,
  onSaved,
  onCancel,
}: {
  vendor: Vendor;
  currentUser: string;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(vendor.name);
  const [status, setStatus] = useState<VendorStatus>(vendor.status);
  const [owner, setOwner] = useState(vendor.owner);
  const [notes, setNotes] = useState(vendor.notes);
  const [saving, setSaving] = useState(false);

  const canSave = name.trim().length > 0;
  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await upsertVendor({
        id: vendor.id || undefined,
        categoryCode: vendor.categoryCode,
        sides: vendor.sides,
        name,
        status,
        owner,
        notes,
        updatedBy: currentUser,
      });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={formWrap}>
      <div style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>
        {vendor.id ? "EDIT VENDOR" : "ADD VENDOR"} · {findCategory(vendor.categoryCode)?.label}
      </div>
      <div style={formGrid}>
        <label style={formLabel}>
          <span style={formLabelText}>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} style={formInput} autoFocus />
        </label>
        <label style={formLabel}>
          <span style={formLabelText}>Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as VendorStatus)}
            style={formInput}
          >
            <option value="live">Live</option>
            <option value="signed">Signed</option>
            <option value="pipeline">Pipeline</option>
            <option value="blocked">Blocked</option>
            <option value="off-list">Off-list</option>
          </select>
        </label>
        <label style={formLabelFull}>
          <span style={formLabelText}>Owner</span>
          <input value={owner} onChange={(e) => setOwner(e.target.value)} style={formInput} />
        </label>
        <label style={formLabelFull}>
          <span style={formLabelText}>Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ ...formInput, minHeight: 40, resize: "vertical" }}
          />
        </label>
      </div>
      <div style={formFoot}>
        <span style={{ flex: 1 }} />
        <button type="button" style={ghostBtn} onClick={onCancel}>Cancel</button>
        <button
          type="button"
          style={canSave ? primaryBtn : primaryBtnDisabled}
          onClick={submit}
          disabled={!canSave || saving}
        >
          {saving ? "Saving…" : vendor.id ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

// =================== Action inline form + read-only line =================

function ActionInlineForm({
  mode,
  row,
  prefillImpacts,
  author,
  onClose,
  onSaved,
}: {
  mode: "add" | "edit";
  row?: ActionRow;
  prefillImpacts?: string[];
  author: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(row?.title ?? "");
  const [owner, setOwner] = useState(row?.owner ?? "");
  const [impacts, setImpacts] = useState<string[]>(row?.impacts ?? prefillImpacts ?? []);
  const [status, setStatus] = useState<ActionStatus>(row?.status ?? "doing");
  const [blockers, setBlockers] = useState(row?.blockers ?? "");
  const [doing, setDoing] = useState(row?.doing ?? "");
  const [dueBy, setDueBy] = useState(row?.dueBy ?? "");
  const [saving, setSaving] = useState(false);

  const canSave = title.trim().length > 0 && owner.trim().length > 0 && impacts.length > 0;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (mode === "add") {
        await addAction({ title, owner, impacts, status, blockers, doing, dueBy, author });
      } else if (row) {
        await updateAction(row.id, { title, owner, impacts, status, blockers, doing, dueBy });
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={formWrap}>
      <div style={{ ...mono, fontSize: 9, color: "var(--ink-faint)" }}>
        {mode === "add" ? "ADD ACTION" : "EDIT ACTION"} · IMPACTS{" "}
        {impacts.map((i) => (
          <span key={i} style={impactChipReadonly}>{i}</span>
        ))}
      </div>
      <div style={formGrid}>
        <label style={formLabelFull}>
          <span style={formLabelText}>Action</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            style={formInput}
            autoFocus
          />
        </label>
        <label style={formLabel}>
          <span style={formLabelText}>Owner</span>
          <input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Who's accountable?"
            style={formInput}
          />
        </label>
        <label style={formLabel}>
          <span style={formLabelText}>By when</span>
          <input
            type="date"
            value={dueBy}
            onChange={(e) => setDueBy(e.target.value)}
            style={formInput}
          />
        </label>
        <label style={formLabel}>
          <span style={formLabelText}>Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ActionStatus)}
            style={formInput}
          >
            <option value="doing">Doing</option>
            <option value="blocked">Blocked</option>
            <option value="watch">Watch</option>
            <option value="done">Done</option>
          </select>
        </label>
        <label style={formLabelFull}>
          <span style={formLabelText}>Blockers</span>
          <textarea
            value={blockers}
            onChange={(e) => setBlockers(e.target.value)}
            style={{ ...formInput, minHeight: 36, resize: "vertical" }}
          />
        </label>
        <label style={formLabelFull}>
          <span style={formLabelText}>What we're doing about it</span>
          <textarea
            value={doing}
            onChange={(e) => setDoing(e.target.value)}
            style={{ ...formInput, minHeight: 36, resize: "vertical" }}
          />
        </label>
      </div>
      <div style={formFoot}>
        <span style={{ flex: 1 }} />
        <button type="button" style={ghostBtn} onClick={onClose}>Cancel</button>
        <button
          type="button"
          style={canSave ? primaryBtn : primaryBtnDisabled}
          onClick={submit}
          disabled={!canSave || saving}
        >
          {saving ? "Saving…" : mode === "add" ? "Save action" : "Update"}
        </button>
      </div>
      {/* impact-required helper retained from the previous UX so the form
          explains itself when prefillImpacts is empty (KPI-wide add). */}
      {impacts.length === 0 && (
        <div style={{ fontSize: 11, color: "var(--amber)", fontFamily: "var(--font-mono)" }}>
          Impacts auto-fill from the row you opened this from. If empty, this action won't save.
        </div>
      )}
    </div>
  );
}

function ActionLineRO({
  row,
  onChanged,
  accent,
}: {
  row: ActionRow;
  onChanged: () => Promise<void>;
  accent: string;
}) {
  const [editing, setEditing] = useState(false);
  const overdue =
    row.dueBy && row.status !== "done" && new Date(row.dueBy) < new Date(todayISO());

  const cycleStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const order: ActionStatus[] = ["doing", "watch", "blocked", "done"];
    const next = order[(order.indexOf(row.status) + 1) % order.length]!;
    await updateAction(row.id, { status: next });
    await onChanged();
  };
  const remove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    await deleteAction(row.id);
    await onChanged();
  };

  if (editing) {
    return (
      <ActionInlineForm
        mode="edit"
        row={row}
        author={row.author}
        onClose={() => setEditing(false)}
        onSaved={async () => {
          await onChanged();
          setEditing(false);
        }}
      />
    );
  }

  return (
    <div style={actionRO} onClick={() => setEditing(true)} title="Click to edit">
      <span style={actionRODot(ACTION_STATUS_COLOR[row.status])} />
      <span style={actionROTitle}>{row.title}</span>
      <span style={actionROMeta}>
        {row.owner || "—"}
        {row.dueBy && (
          <>
            {" · "}
            <span style={overdue ? { color: "var(--coral)" } : undefined}>{row.dueBy}</span>
          </>
        )}
        {!row.synced && <span style={localBadge}>local</span>}
      </span>
      <span style={impactsLine}>
        {row.impacts.map((i) => (
          <span key={i} style={{ ...impactChipMini, borderColor: accent, color: accent }}>{i}</span>
        ))}
      </span>
      <button type="button" style={statusPillBtn(row.status)} onClick={cycleStatus}>
        {ACTION_STATUS_LABEL[row.status]}
      </button>
      <button type="button" style={deleteBtn} onClick={remove} title="Delete">×</button>
    </div>
  );
}

// =================== Side-filtered ledger =================================

function SideLedger({
  actions,
  currentUser,
  onChange,
}: {
  actions: ActionRow[];
  currentUser: string;
  onChange: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div style={ledgerWrap}>
      <div style={ledgerHead}>
        <div>
          <div style={{ ...mono, color: "var(--ink-faint)" }}>ACCOUNTABILITY LEDGER · THIS SIDE</div>
          <h2 style={ledgerTitle}>Everything in flight</h2>
        </div>
        <button type="button" style={addBtnLarge} onClick={() => setAdding((v) => !v)}>
          {adding ? "Cancel" : "+ Add action"}
        </button>
      </div>

      {adding && (
        <ActionInlineForm
          mode="add"
          prefillImpacts={[]}
          author={currentUser}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            await onChange();
            setAdding(false);
          }}
        />
      )}

      <div>
        <div style={ledgerColHead}>
          <span>ACTION</span>
          <span>IMPACTS</span>
          <span>OWNER</span>
          <span>STATUS</span>
          <span>BLOCKERS</span>
          <span>DOING</span>
          <span>BY WHEN</span>
          <span></span>
        </div>
        {actions.length === 0 ? (
          <div style={emptyState}>No actions on this side yet.</div>
        ) : (
          actions.map((a) =>
            editingId === a.id ? (
              <div key={a.id} style={{ padding: "4px 0" }}>
                <ActionInlineForm
                  mode="edit"
                  row={a}
                  author={a.author}
                  onClose={() => setEditingId(null)}
                  onSaved={async () => {
                    await onChange();
                    setEditingId(null);
                  }}
                />
              </div>
            ) : (
              <LedgerRow
                key={a.id}
                row={a}
                onEdit={() => setEditingId(a.id)}
                onChanged={onChange}
              />
            ),
          )
        )}
      </div>
    </div>
  );
}

function LedgerRow({
  row,
  onEdit,
  onChanged,
}: {
  row: ActionRow;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const overdue =
    row.dueBy && row.status !== "done" && new Date(row.dueBy) < new Date(todayISO());
  const cycleStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const order: ActionStatus[] = ["doing", "watch", "blocked", "done"];
    const next = order[(order.indexOf(row.status) + 1) % order.length]!;
    await updateAction(row.id, { status: next });
    await onChanged();
  };
  const remove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    await deleteAction(row.id);
    await onChanged();
  };
  return (
    <div style={ledgerRow} onClick={onEdit} title="Click to edit">
      <span style={ledgerAction}>{row.title}</span>
      <span style={ledgerImpacts}>
        {row.impacts.map((i) => (
          <span key={i} style={impactChipMini}>{i}</span>
        ))}
      </span>
      <span style={ledgerEllipsis}>{row.owner || "—"}</span>
      <button type="button" style={statusPillBtn(row.status)} onClick={cycleStatus}>
        {ACTION_STATUS_LABEL[row.status]}
      </button>
      <span style={ledgerEllipsis} title={row.blockers}>{row.blockers || "—"}</span>
      <span style={ledgerEllipsis} title={row.doing}>{row.doing || "—"}</span>
      <span style={overdue ? { ...ledgerDue, color: "var(--coral)" } : ledgerDue}>
        {row.dueBy || "—"}
        {!row.synced && <span style={localBadge}>local</span>}
      </span>
      <button type="button" style={deleteBtn} onClick={remove}>×</button>
    </div>
  );
}

// =================== Headline ===========================================

function Headline({ rv }: { rv: ResolvedValue | undefined }) {
  const text = rv?.text ?? "—";
  const unit = rv?.unit;
  const state = rv?.state ?? "awaiting";
  const isCurrency = unit === "currency";
  return (
    <div style={headlineWrap}>
      <span style={headlineValue}>
        {isCurrency && state !== "awaiting" && state !== "error" ? "₦ " : ""}
        {text}
      </span>
      {unit === "coins" && <span style={unitChip("var(--purple)")}>COINS</span>}
      {unit === "ratio" && <span style={unitChip("var(--ink-dim)")}>RATIO</span>}
      <span style={statePill(stateLabel(state), state)}>{stateLabel(state)}</span>
    </div>
  );
}

// =================== helpers ============================================

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function stateLabel(state: string): string {
  return state === "live" ? "LIVE" : state === "sample" ? "SAMPLE" : state === "error" ? "ERROR" : "AWAITING";
}
function stateColor(state: string): string {
  return state === "live"
    ? "var(--purple)"
    : state === "sample"
      ? "var(--amber)"
      : state === "error"
        ? "var(--coral)"
        : "var(--ink-faint)";
}

// =================== styles =============================================

const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 700,
};

function rowDot(color: string): React.CSSProperties {
  return {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: color,
    boxShadow: `0 0 5px ${color}`,
    flexShrink: 0,
  };
}

const kpiStack: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const sectionWrap: React.CSSProperties = {
  background: "var(--panel-2)",
  border: "1px solid var(--edge)",
  borderRadius: 0,
  boxShadow: "var(--shadow-card)",
  display: "flex",
  flexDirection: "column",
};
const sectionHead: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 18px",
  borderBottom: "1px solid var(--edge)",
};
function kpiChip(accent: string): React.CSSProperties {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: "var(--bg-0)",
    background: accent,
    borderRadius: 4,
    padding: "5px 9px",
  };
}
const sectionTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: "var(--ink)",
};
const sectionSubtitle: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-faint)",
  marginTop: 2,
};

const sectionBody: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 5fr) minmax(0, 4fr)",
  gap: 18,
  padding: "16px 18px",
  borderBottom: "1px solid var(--edge)",
};
const chartCol: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const chartPlaceholder: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 200,
  border: "1px dashed var(--edge)",
};
const categoryCol: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const categoryList: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const categoryBlock: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
function categoryRow(open: boolean, accent: string): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    background: open ? "rgba(255,255,255,0.03)" : "transparent",
    border: "1px solid var(--edge)",
    borderLeft: open ? `2px solid ${accent}` : "1px solid var(--edge)",
    borderRadius: 4,
    cursor: "pointer",
    color: "var(--ink)",
    textAlign: "left" as const,
  };
}
function caret(open: boolean): React.CSSProperties {
  return {
    color: "var(--ink-faint)",
    fontSize: 10,
    transform: open ? "rotate(90deg)" : "rotate(0deg)",
    transition: "transform 0.15s",
    flexShrink: 0,
    width: 12,
    display: "inline-block",
  };
}
const categoryLabel: React.CSSProperties = {
  flex: 1,
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--ink)",
};
const categoryStats: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};
const statBlock: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.06em",
  color: "var(--ink-dim)",
};
const categoryBody: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  margin: "4px 0 8px 24px",
  paddingLeft: 8,
  borderLeft: "1px dashed var(--edge)",
};
const categoryDesc: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-faint)",
  padding: "4px 0 8px",
  fontStyle: "italic",
};
function priorityChip(priority: "p0" | "p1" | "p2"): React.CSSProperties {
  const color = PRIORITY_COLOR[priority];
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color,
    border: `1px solid ${color}`,
    borderRadius: 3,
    padding: "1px 5px",
    flexShrink: 0,
  };
}
const ownerChip: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: "var(--purple)",
  background: "rgba(83, 74, 183, 0.14)",
  padding: "2px 7px",
  borderRadius: 3,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 220,
  flexShrink: 1,
};
const categoryAnchor: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-dim)",
  padding: "0 0 6px",
  lineHeight: 1.5,
};

// ---- per-block metric column (the y-axis that changes per KPI) ------------

interface BlockMetric {
  text: string;
  sub?: string;
  tone: "good" | "warn" | "bad" | "neutral" | "antipattern";
}

function computeBlockMetric(
  key: CategoryMetricKey | undefined,
  cat: Category,
  effortCount: number,
  fit: CategoryFit,
): BlockMetric {
  switch (key) {
    case "effortCount":
      return {
        text: String(effortCount),
        sub: effortCount === 1 ? "action" : "actions",
        tone: effortCount > 0 ? "good" : "neutral",
      };
    case "first365d":
      return {
        text: cat.metrics.first365d.toLocaleString(),
        sub: "365d",
        tone: cat.antipattern
          ? "antipattern"
          : cat.metrics.first365d >= 1000
            ? "good"
            : cat.metrics.first365d >= 100
              ? "warn"
              : "neutral",
      };
    case "retain14dPct":
      return {
        text: cat.metrics.retain14dPct.toFixed(1) + "%",
        sub: "14d",
        tone: cat.antipattern
          ? "antipattern"
          : cat.metrics.retain14dPct >= 70
            ? "good"
            : cat.metrics.retain14dPct >= 40
              ? "warn"
              : "bad",
      };
    case "retainSameDayPct":
      return {
        text: cat.metrics.retainSameDayPct.toFixed(1) + "%",
        sub: "same-day",
        tone: cat.metrics.retainSameDayPct >= 40 ? "good" : "neutral",
      };
    case "everReturnedPct":
      return {
        text: cat.metrics.everReturnedPct.toFixed(0) + "%",
        sub: "ever",
        tone: cat.metrics.everReturnedPct >= 70 ? "good" : "neutral",
      };
    case "fit":
      return {
        text: FIT_LABEL[fit],
        tone:
          fit === "proven" || fit === "strong"
            ? "good"
            : fit === "plausible" || fit === "signal"
              ? "warn"
              : fit === "antipattern"
                ? "antipattern"
                : "bad",
      };
    default:
      return { text: "—", tone: "neutral" };
  }
}

function fitChip(fit: CategoryFit): React.CSSProperties {
  const color = FIT_COLOR[fit];
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color,
    border: `1px solid ${color}`,
    borderRadius: 3,
    padding: "1px 5px",
    flexShrink: 0,
    whiteSpace: "nowrap",
  };
}

function blockMetricStyle(tone: BlockMetric["tone"]): React.CSSProperties {
  const color =
    tone === "good"
      ? "#5ec98c"
      : tone === "warn"
        ? "var(--amber)"
        : tone === "bad" || tone === "antipattern"
          ? "var(--coral)"
          : "var(--ink-dim)";
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.04em",
    color,
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    minWidth: 70,
    justifyContent: "flex-end",
  };
}
const blockMetricValue: React.CSSProperties = {
  fontWeight: 700,
};
const blockMetricSub: React.CSSProperties = {
  fontSize: 9,
  opacity: 0.7,
};

const categoryColHead: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: 6,
};
const categoryMetricColHead: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.12em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
};

// ---- strategic call band (the intelligence layer per block) ---------------

function strategicCallBand(accent: string): React.CSSProperties {
  return {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: "10px 14px 12px",
    borderTop: "1px solid var(--edge)",
    borderBottom: "1px solid var(--edge)",
    background: `linear-gradient(90deg, ${accent}11 0%, transparent 70%)`,
  };
}
function strategicCallTag(accent: string): React.CSSProperties {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    letterSpacing: "0.14em",
    fontWeight: 700,
    color: accent,
    flexShrink: 0,
    paddingTop: 1,
  };
}
const strategicCallText: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--ink)",
  lineHeight: 1.5,
  flex: 1,
};

const vendorWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
};
const vendorRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 8px",
  cursor: "pointer",
  borderTop: "1px solid var(--edge)",
};
const vendorName: React.CSSProperties = {
  flex: 1,
  fontSize: 12,
  color: "var(--ink)",
  fontWeight: 500,
};
function vendorStatusPill(s: VendorStatus): React.CSSProperties {
  const c = VENDOR_STATUS_COLOR[s];
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 8,
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: c,
    background: "transparent",
    border: `1px solid ${c}`,
    borderRadius: 3,
    padding: "2px 5px",
    flexShrink: 0,
  };
}
const vendorOwner: React.CSSProperties = {
  fontSize: 11,
  color: "var(--ink-dim)",
  minWidth: 80,
  textAlign: "right" as const,
};
function vendorActionCount(accent: string): React.CSSProperties {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    fontWeight: 700,
    color: accent,
    background: "transparent",
    border: `1px solid ${accent}`,
    borderRadius: 3,
    padding: "1px 5px",
  };
}
const pencilBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--edge)",
  color: "var(--ink-faint)",
  borderRadius: 3,
  width: 20,
  height: 20,
  fontSize: 10,
  cursor: "pointer",
  padding: 0,
  flexShrink: 0,
};
const vendorBody: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: "6px 8px 8px 24px",
  borderTop: "1px solid var(--edge)",
  background: "rgba(0,0,0,0.15)",
};
const vendorNotes: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-dim)",
  fontStyle: "italic",
  paddingBottom: 4,
  borderBottom: "1px dashed var(--edge)",
};
const vendorActionsHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingTop: 4,
};

const addVendorBtn: React.CSSProperties = {
  alignSelf: "flex-start",
  margin: "8px 0 0",
  background: "transparent",
  border: "1px dashed var(--edge)",
  color: "var(--ink-faint)",
  borderRadius: 4,
  padding: "5px 9px",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.06em",
  cursor: "pointer",
};

const kpiActionsBar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  borderBottom: "1px solid var(--edge)",
};
const addBtnSmall: React.CSSProperties = {
  background: "rgba(122, 63, 176, 0.14)",
  border: "1px solid var(--purple)",
  color: "var(--purple-soft)",
  borderRadius: 4,
  padding: "4px 9px",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontFamily: "var(--font-mono)",
  cursor: "pointer",
};
const addBtnLarge: React.CSSProperties = {
  background: "rgba(122, 63, 176, 0.16)",
  border: "1px solid var(--purple)",
  color: "var(--purple-soft)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontFamily: "var(--font-mono)",
  cursor: "pointer",
};

const actionRO: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 18px",
  borderTop: "1px solid var(--edge)",
  cursor: "pointer",
  fontSize: 12,
};
function actionRODot(color: string): React.CSSProperties {
  return {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: color,
    boxShadow: `0 0 4px ${color}`,
    flexShrink: 0,
  };
}
const actionROTitle: React.CSSProperties = {
  flex: 1,
  color: "var(--ink)",
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};
const actionROMeta: React.CSSProperties = {
  fontSize: 10.5,
  color: "var(--ink-faint)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  gap: 4,
};
const impactsLine: React.CSSProperties = {
  display: "flex",
  gap: 3,
  flexWrap: "wrap",
};
const impactChipMini: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 8,
  fontWeight: 700,
  color: "var(--purple-soft)",
  background: "rgba(122, 63, 176, 0.12)",
  border: "1px solid rgba(122, 63, 176, 0.3)",
  borderRadius: 3,
  padding: "1px 4px",
};
const impactChipReadonly: React.CSSProperties = {
  ...impactChipMini,
  marginLeft: 4,
};
const localBadge: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 8,
  color: "var(--amber)",
  border: "1px solid var(--amber)",
  borderRadius: 3,
  padding: "0 3px",
  marginLeft: 4,
};
function statusPillBtn(s: ActionStatus): React.CSSProperties {
  const c = ACTION_STATUS_COLOR[s];
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: "0.1em",
    color: c,
    background: "transparent",
    border: `1px solid ${c}`,
    borderRadius: 4,
    padding: "3px 6px",
    cursor: "pointer",
    flexShrink: 0,
  };
}
const deleteBtn: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--ink-faint)",
  fontSize: 16,
  cursor: "pointer",
  padding: 0,
  width: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const headlineWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
};
const headlineValue: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontWeight: 700,
  color: "var(--ink)",
  fontSize: 18,
  lineHeight: 1,
};
function unitChip(color: string): React.CSSProperties {
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    fontWeight: 700,
    color,
    background: "transparent",
    border: `1px solid ${color}`,
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.12em",
  };
}
function statePill(_label: string, state: string): React.CSSProperties {
  const c = stateColor(state);
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 8,
    fontWeight: 700,
    color: c,
    background: "transparent",
    border: `1px solid ${c}`,
    borderRadius: 4,
    padding: "2px 6px",
    letterSpacing: "0.12em",
  };
}

const ledgerWrap: React.CSSProperties = {
  marginTop: 28,
  background: "var(--panel-2)",
  border: "1px solid var(--edge)",
  padding: "18px 20px 16px",
  boxShadow: "var(--shadow-card)",
};
const ledgerHead: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 14,
  paddingBottom: 14,
  borderBottom: "1px solid var(--edge)",
};
const ledgerTitle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  margin: "4px 0 0",
  color: "var(--ink)",
};
const ledgerColHead: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "2fr 1.2fr 1fr 80px 1.5fr 1.5fr 90px 28px",
  gap: 12,
  padding: "8px 4px",
  ...mono,
  fontSize: 9,
  color: "var(--ink-faint)",
  borderBottom: "1px solid var(--edge)",
};
const ledgerRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "2fr 1.2fr 1fr 80px 1.5fr 1.5fr 90px 28px",
  gap: 12,
  padding: "10px 4px",
  fontSize: 12,
  color: "var(--ink-dim)",
  borderBottom: "1px solid var(--edge)",
  alignItems: "center",
  cursor: "pointer",
};
const ledgerAction: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--ink)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const ledgerImpacts: React.CSSProperties = {
  display: "flex",
  gap: 3,
  flexWrap: "wrap",
};
const ledgerEllipsis: React.CSSProperties = {
  fontSize: 12,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const ledgerDue: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--ink-dim)",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const emptyState: React.CSSProperties = {
  padding: "12px 0",
  color: "var(--ink-faint)",
  fontSize: 12,
  fontStyle: "italic",
  textAlign: "center" as const,
};

// form
const formWrap: React.CSSProperties = {
  background: "rgba(122, 63, 176, 0.06)",
  border: "1px solid rgba(122, 63, 176, 0.28)",
  borderRadius: 6,
  padding: "10px 12px",
  marginTop: 6,
  marginBottom: 6,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const formGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 8,
};
const formLabel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};
const formLabelFull: React.CSSProperties = {
  ...formLabel,
  gridColumn: "1 / -1",
};
const formLabelText: React.CSSProperties = {
  ...mono,
  fontSize: 9,
  color: "var(--ink-faint)",
};
const formInput: React.CSSProperties = {
  background: "var(--bg-1)",
  border: "1px solid var(--edge)",
  borderRadius: 4,
  padding: "6px 8px",
  color: "var(--ink)",
  fontSize: 12,
  fontFamily: "var(--font-display)",
  outline: "none",
  letterSpacing: "normal",
  textTransform: "none",
  fontWeight: 400,
};
const formFoot: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};
const primaryBtn: React.CSSProperties = {
  background: "linear-gradient(135deg, var(--purple-soft), var(--purple-deep))",
  border: 0,
  color: "var(--bg-0)",
  borderRadius: 4,
  padding: "7px 14px",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  fontFamily: "var(--font-mono)",
  cursor: "pointer",
};
const primaryBtnDisabled: React.CSSProperties = {
  ...primaryBtn,
  background: "var(--edge)",
  color: "var(--ink-faint)",
  cursor: "not-allowed",
};
const ghostBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--edge)",
  color: "var(--ink-dim)",
  borderRadius: 4,
  padding: "7px 12px",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  cursor: "pointer",
};
